import { CM } from "@pdfking/quantity"

import type { GrowLayout, GrowTemplate, PrimaryKey } from "#public"
import { ContentRenderer } from "#public"

export type GrowKindFlags = Partial<
  Pick<
    GrowTemplate,
    | "is_grow"
    | "is_book"
    | "is_cover"
    | "is_fold"
    | "is_box"
    | "is_single_page"
    | "show_cut"
    | "show_crossmark"
    | "show_security"
    | "show_crossmark_on_fold"
    | "renderer"
  >
>

/** What a grow may say beyond its flags and its tree. */
export type GrowKindData = Partial<
  Pick<GrowTemplate, "sides_tuple" | "depth_molt" | "maximum_tickness" | "svg_round_corner_size">
>

const GROW_DEFAULTS: Required<GrowKindFlags> = {
  is_grow: true,
  is_book: false,
  is_cover: false,
  is_fold: false,
  is_box: false,
  is_single_page: false,
  show_cut: true,
  show_crossmark: true,
  show_security: true,
  show_crossmark_on_fold: false,
  renderer: ContentRenderer.DEPLIANT,
}

export const makeGrowEntry = <I extends string>(
  id: I,
  name: string,
  label: string,
  layout: GrowLayout,
  flags: GrowKindFlags = {},
  data: GrowKindData = {}
): GrowTemplate & PrimaryKey<I> => ({
  name,
  label,
  svg_round_corner_size: 0,
  depth_molt: 1,
  maximum_tickness: CM(100),
  sides_tuple: [],
  ...GROW_DEFAULTS,
  ...flags,
  ...data,
  layout,
  id,
})

export const makeFoldEntry = <I extends string>(
  id: I,
  name: string,
  layout: GrowLayout,
  data: GrowKindData = {}
): GrowTemplate & PrimaryKey<I> =>
  makeGrowEntry(
    id,
    name,
    "FOLD",
    layout,
    {
      is_single_page: true,
      is_fold: true,
      show_crossmark_on_fold: false,
    },
    data
  )

export const makeCoverEntry = <I extends string>(
  id: I,
  name: string,
  layout: GrowLayout,
  flags: GrowKindFlags = {},
  data: GrowKindData = {}
): GrowTemplate & PrimaryKey<I> =>
  makeGrowEntry(
    id,
    name,
    "COVER",
    layout,
    {
      is_single_page: true,
      is_cover: true,
      show_crossmark_on_fold: true,
      renderer: ContentRenderer.BOOK,
      ...flags,
    },
    data
  )
